import {
  Accordion,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { openConfirmModal, openContextModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import {
  IconChevronsUp,
  IconClock,
  IconCopy,
  IconSettings,
  IconStars,
  IconTable,
  IconTrash,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useTranslation } from "next-i18next";
import router from "next/router";
import { trackEvent } from "../../common/analytics";
import {
  downloadEventExport,
  getOrganisation,
  removeEventReferee,
  updateEvent,
  uploadEventImage,
} from "../../common/api";
import { useBoard, useIsBoardAdmin } from "../../common/boards/board.hooks";
import {
  Event,
  EventType,
  MAX_MEMBERS,
  MAX_REGISTRATION_PRICE,
  RecurringType,
  RecurringUnit,
  SeedType,
} from "../../common/events/event.schema";
import { Member } from "../../common/members/member.schema";
import { PlanType } from "../../common/users/user.schema";
import {
  getBoardCurrencySymbol,
  getBoardLink,
  getBoardPlan,
  getRecordId,
} from "../../common/util";
import DateTimeProp from "../inputs/DateTimeProp";
import ImageInput from "../inputs/ImageInput";
import NumberProp from "../inputs/NumberProp";
import SelectProp from "../inputs/SelectProp";
import SwitchProp from "../inputs/SwitchProp";
import TextProp from "../inputs/TextProp";
import PlanPrompt from "../layout/PlanPrompt";
import ImportMembers from "../members/ImportMembers";
import PaymentPromptBar from "../payments/PaymentPromptBar";
import UserDisplay from "../users/UserDisplay";
import CheckInQR from "./CheckInQR";
import {
  getEventName,
  getHasEliminations,
  getHasPools,
  getHasTeams,
  getIsTournament,
  getIsUSFAWeapon,
} from "./util";
import { useEventStore } from "../../common/events/event.store";
import { State } from "./addevent/AddEventModal";
import { listTokenTypes } from "../../common/payments/payments.api";
import { Sport, Weapon } from "../../common/sports";

// interface Props {
//   board?: Board;
// }
const Settings = function ({ event }: { event: Event }) {
  const { t } = useTranslation();
  // const router = useRouter();
  // let { boardShortLink } = router.query;
  const queryClient = useQueryClient();
  const { board } = useBoard();
  const isAdmin = useIsBoardAdmin();
  const refetch = () => {
    queryClient.invalidateQueries(["event", event.shortLink]);
    queryClient.refetchQueries(["event", event.shortLink]);
  };
  const { mutate } = useMutation(updateEvent, {
    onSuccess: refetch,
  });
  const { data: tokenTypeRes } = useQuery(["tokenTypes", board?._id], () =>
    listTokenTypes({ params: { board: board?._id } })
  );
  const tokenTypeOptions = tokenTypeRes?.records?.map((t) => ({
    value: t._id,
    label: t.name,
  }));
  const { data: organisation } = useQuery(
    ["organisation", board?.organisation],
    () => {
      if (board) return getOrganisation(getRecordId(board.organisation));
    }
  );
  //   useRequireBoardAdmin();
  const { mutate: upload } = useMutation(uploadEventImage);
  const setImage = async (image: Blob) => {
    await upload(
      { id: event._id, image },
      {
        onSuccess: () => {
          showNotification({
            message: t("Image uploaded"),
            autoClose: true,
            color: "success",
          });
          refetch();
        },
      }
    );
  };
  let memberLimit = board ? getBoardPlan(board).eventSize : 99;
  if (memberLimit < 0) memberLimit = MAX_MEMBERS;
  const activeMembers = (event.members as Member[]).filter(
    (member) => event.withdrawals.indexOf(member._id) === -1
  );
  // console.log("activeMembers :", activeMembers);
  const showWithdraw = () => {
    trackEvent("open_modal_retire_member", { board: board?._id });
    openContextModal({
      title: "Select a member to retire",
      innerProps: {
        activeMembers,
        event,
      },
      modal: "retireMember",
    });
  };
  const showLateEntry = () => {
    trackEvent("open_modal_add_late_entry", { board: board?._id });
    openContextModal({
      title: "Select a late entry to add",
      innerProps: {
        exclude: event.members,
        event,
        creatable: isAdmin,
      },
      modal: "addEventMember",
    });
  };
  const onClickDownloadUSFA = () => {
    const invalidMembers = (event.members as Member[]).filter(
      (m) =>
        !(
          m.name &&
          m.name?.length > 0 &&
          m.lastName &&
          m.lastName?.length > 0 &&
          m.usfaMemberNumber &&
          m.usfaMemberNumber?.length > 0
        )
    );
    if (invalidMembers.length === 0) {
      doDownloadUSFA();
    } else {
      // console.log("invalid members", invalidMembers);
      trackEvent("open_modal_invalid_import", { board: board?._id });
      openContextModal({
        title: t("Invalid members"),
        modal: "invalidUSFAMembers",
        size: "xl",
        innerProps: {
          invalidMembers,
          event,
        },
      });
    }
  };
  const { mutate: removeReferee, isLoading: isLoadingRemoveRef } = useMutation(
    removeEventReferee,
    {
      onSuccess: refetch,
    }
  );
  const onImport = (members: Member[]) => {
    // console.log("on import", members);
    const existing = event.members as Member[];
    for (let i = 0; i < members.length; i++) {
      const member = members[i];
      if (existing.find((m) => m._id === member._id)) continue;
      existing.push(member);
    }
    const ids = existing.map((m) => m._id).slice(0, memberLimit);
    mutate({ _id: event._id, members: ids });
  };
  const doDownloadUSFA = () => {
    downloadEventExport({ id: event._id, format: "usfa" });
  };
  const hasPools = getHasPools(event);
  const hasTeams = getHasTeams(event);

  const setFormState = useEventStore((s) => s.setFormState);
  const hasTeamPlan = board?.plan === PlanType.TEAM;
  const enableLateEntry = !event.started || (hasPools && !event.poolsComplete);
  // console.log("enableLateEntry :", enableLateEntry);
  const canSelectWeapon = !board?.weapon || board.weapon === Weapon.MIXED;
  const scheduledDate = event.scheduledDate
    ? new Date(event.scheduledDate)
    : new Date();
  const dayOfWeek = format(scheduledDate, "eeee");
  // const weekTh = ordinal(Math.ceil(getDate(scheduledDate) / 7));
  const scheduleChangeAllowed = !event.started;
  const isTournament = event.eventType === EventType.TOURNAMENT;
  const seedOptions = [
    { value: SeedType.SCORE, label: "Score" },
    { value: SeedType.MANUAL, label: "Manual" },
    { value: SeedType.RANDOM, label: "Random" },
  ];
  if (getIsUSFAWeapon(event.weapon))
    seedOptions.push({ value: SeedType.USFA, label: "USFA rating" });
  const weaponOptions = [
    { value: Weapon.EPEE, label: "Epee" },
    { value: Weapon.FOIL, label: "Foil" },
    { value: Weapon.SABRE, label: "Sabre" },
    { value: Weapon.HEMA_RAPIER, label: "Rapier (HEMA)" },
    { value: Weapon.HEMA_LONGSWORD, label: "Longsword (HEMA)" },
    { value: Weapon.HEMA_SABRE, label: "Sabre (HEMA)" },
    { value: Weapon.HEMA_RAPIER_DAGGER, label: "Rapier & dagger (HEMA)" },
    { value: Weapon.HEMA_SWORD_BUCKLER, label: "Sword & buckler (HEMA)" },
  ];
  let roundOptions = [
    { value: "1", label: "1 round of 2 players" },
    { value: "2", label: "2 rounds of 4 players" },
    { value: "3", label: "3 rounds of 8 players" },
    { value: "4", label: "4 rounds of 16 players" },
    { value: "5", label: "5 rounds of 32 players" },
    { value: "6", label: "6 rounds of 64 players" },
    { value: "7", label: "7 rounds of 128 players" },
    { value: "8", label: "8 rounds of 256 players" },
  ];
  const poolOptions = [
    { value: "2", label: "pools of 2" },
    { value: "3", label: "pools of 3" },
    { value: "4", label: "pools of 4" },
    { value: "5", label: "pools of 5" },
    { value: "6", label: "pools of 6" },
    { value: "7", label: "pools of 7" },
    { value: "8", label: "pools of 8" },
  ];
  if (board?.plan === PlanType.FREE) {
    //only allow 3 rounds for free plan
    roundOptions = roundOptions.slice(0, 3);
  }
  let disablePlayoffSwitch = event.complete;
  if (event.eliminations.length > 0) {
    const playoffRound = event.eliminations[event.eliminations.length - 1];
    if (playoffRound.matches.length > 0) {
      disablePlayoffSwitch = true;
    }
  }
  const showCloneModal = () => {
    setFormState({
      startState: State.SUMMARY,
      // name: event.name,
      hasTeams: hasTeams,
      scheduled: !!event.scheduledDate,
      manualTeams: hasTeams,
      privateLesson: event.maxMembers === 1,
      board: getRecordId(event.board),
      eventType: event.eventType,
      weapon: event.weapon,
      members: (event.members as Member[]).map((m) => m._id),
      teams: event.teams as string[],
      seedType: event.seedType,
      type: event.type,
      requirePoolPoints: event.requirePoolPoints,
      eliminationRounds: event.eliminationRounds,
      poolSize: event.poolSize,
      playoff: event.playoff,
      recurringType: event.recurringType,
      // recurringUnit: event.recurringUnit,
      // recurringSeperation: event.recurringSeperation,
      openRegistration: event.openRegistration,
      requireCheckIn: event.requireCheckIn,
      autoApproveRegistrations: event.autoApproveRegistrations,
      requireRegistrationPayment: event.requireRegistrationPayment,
      registrationPrice: event.registrationPrice,
      tokenType: event.tokenType ? getRecordId(event.tokenType) : undefined,
      maxMembers: event.maxMembers,
      requireFullName: event.requireFullName,
      // requireUsfaMemberNumber: event.requireUSFA,
      tempTeams: event.teams as Member[],
    });
    trackEvent("open_modal_clone_event", {
      board: board?._id,
    });
    openContextModal({
      title: isTournament ? "Copy tournament" : "Copy class",
      modal: "addEvent",
      fullScreen: true,
      innerProps: {
        // creatable: isAdmin || (isRef && board?.refereeAddMembers),
      },
    });
  };
  return (
    <Container size="sm" pt={10} px={0}>
      <Card>
        <Accordion defaultValue="settings">
          <Accordion.Item value="settings">
            <Accordion.Control>
              <Group>
                <ThemeIcon variant="light">
                  <IconSettings />
                </ThemeIcon>
                <Text size="sm">Settings</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <ImageInput currentImage={event.image} onChange={setImage} />
                <TextProp
                  fullWidth
                  object={event}
                  prop="name"
                  required
                  mutate={mutate}
                  label={t("Event name")}
                />
                {canSelectWeapon && (
                  <SelectProp
                    object={event}
                    prop="weapon"
                    mutate={mutate}
                    options={weaponOptions}
                    label={t("Weapon")}
                    tooltip={t("Change event weapon")}
                  />
                )}
                {!event.archived && (
                  <Button
                    leftSection={<IconTrash size={15} />}
                    variant="default"
                    onClick={() =>
                      openConfirmModal({
                        title: t("Are you sure you want to archive this event"),
                        children: (
                          <Text size="sm">
                            This event will no longer be visible to other users
                          </Text>
                        ),
                        labels: { confirm: "Confirm", cancel: "Cancel" },
                        confirmProps: { color: "red" },
                        onConfirm: () => {
                          mutate({ _id: event._id, archived: true });
                          queryClient.refetchQueries(["events"]);
                          if (board)
                            router.push(`${getBoardLink(board)}/events`);
                        },
                      })
                    }
                  >
                    {t("Archive event")}
                  </Button>
                )}

                <Button
                  leftSection={<IconCopy size={15} />}
                  variant="default"
                  onClick={() => {
                    showCloneModal();
                    // openConfirmModal({
                    //   title: t("Are you sure you want to copy this event"),
                    //   labels: { confirm: "Confirm", cancel: "Cancel" },
                    //   confirmProps: { color: "red" },
                    //   onConfirm: () => {
                    //     showCloneModal();
                    //     // console.log("event.members", event.members);
                    //   },
                    // });
                  }}
                >
                  {t("Copy event")}
                </Button>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          {isTournament && (
            <Accordion.Item value="format">
              <Accordion.Control disabled={event.complete}>
                <Group>
                  <ThemeIcon variant="light">
                    <IconTable />
                  </ThemeIcon>
                  <Text size="sm">Format</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <SelectProp
                    object={event}
                    prop="seedType"
                    mutate={mutate}
                    options={seedOptions}
                    label={t("Seed type")}
                    tooltip={t("Change seed type")}
                  />
                  {getHasPools(event) && (
                    <SelectProp
                      object={event}
                      prop="poolSize"
                      mutate={mutate}
                      disabled={event.matches.length > 0}
                      options={poolOptions}
                      label={t("Pool maximum size")}
                      tooltip={t("Change pool size type")}
                    />
                  )}
                  {getHasEliminations(event) && (
                    <SelectProp
                      object={event}
                      prop="eliminationRounds"
                      mutate={mutate}
                      options={roundOptions}
                      disabled={event.eliminations[0]?.matches.length > 0}
                      label={t("Pool elimination size")}
                      tooltip={t("Change elimination size type")}
                    />
                  )}
                  {getHasEliminations(event) && (
                    <SwitchProp
                      object={event}
                      prop="playoff"
                      label={t("Playoff for third place")}
                      mutate={mutate}
                      disabled={disablePlayoffSwitch}
                    />
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {!hasTeams && (
            <Accordion.Item value="schedule">
              <Accordion.Control disabled={event.started}>
                <Group>
                  <ThemeIcon variant="light">
                    <IconClock />
                  </ThemeIcon>
                  <Text size="sm">Schedule</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  {!scheduleChangeAllowed && (
                    <Text size="sm">
                      Event schedule can no longer be edited
                    </Text>
                  )}

                  {scheduleChangeAllowed && (
                    <Text
                      color={scheduleChangeAllowed ? "white" : "dimmed"}
                      size="sm"
                    >
                      Event schedule
                    </Text>
                  )}
                  <DateTimeProp
                    disabled={!scheduleChangeAllowed}
                    object={event}
                    prop="scheduledDate"
                    minDate={new Date()}
                    dropdownType="modal"
                    mutate={mutate}
                    fullWidth
                  />
                  <Text
                    color={scheduleChangeAllowed ? "white" : "dimmed"}
                    size="sm"
                  >
                    Recurring event
                  </Text>
                  <SelectProp
                    disabled={!scheduleChangeAllowed}
                    object={event}
                    mutate={mutate}
                    prop="recurringType"
                    options={[
                      {
                        value: RecurringType.NONE,
                        label: "Does not repeat",
                      },
                      { value: RecurringType.DAILY, label: "Daily" },
                      {
                        value: RecurringType.WEEKDAY,
                        label: `Weekly on a ${dayOfWeek}`,
                      },
                      // {
                      //   value: RecurringType.NTH_WEEKDAY_OF_MONTH,
                      //   label: `Monthly on the ${weekTh} ${dayOfWeek}`,
                      // },
                      {
                        value: RecurringType.ANNUALLY,
                        label: `Annually on ${format(scheduledDate, "MMMM d")}`,
                      },
                      { value: RecurringType.CUSTOM, label: `Custom...` },
                    ]}
                  />

                  {event.recurringType === RecurringType.CUSTOM && (
                    <Group wrap="nowrap">
                      <Text w={50} size="xs">
                        Every
                      </Text>
                      <NumberProp
                        disabled={!scheduleChangeAllowed}
                        object={event}
                        mutate={mutate}
                        prop="recurringSeperation"
                        w={100}
                      />
                      <SelectProp
                        disabled={!scheduleChangeAllowed}
                        object={event}
                        mutate={mutate}
                        prop="recurringUnit"
                        options={[
                          { value: RecurringUnit.DAY, label: "days" },
                          { value: RecurringUnit.WEEK, label: "weeks" },
                          { value: RecurringUnit.MONTH, label: "months" },
                        ]}
                      />
                    </Group>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {!hasTeams && (
            <Accordion.Item value="users">
              <Accordion.Control disabled={event.complete}>
                <Group>
                  <ThemeIcon variant="light">
                    <IconUsers />
                  </ThemeIcon>
                  <Text size="sm">Users and entries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  {event.complete && (
                    <>
                      <Text size="sm">
                        {getEventName(event)} already complete
                      </Text>
                    </>
                  )}
                  {!event.complete && (
                    <>
                      {enableLateEntry && event.started && (
                        <Button
                          leftSection={<IconUserPlus size={15} />}
                          variant="default"
                          onClick={showLateEntry}
                        >
                          Add a late event entry
                        </Button>
                      )}
                      {isTournament && event.started && (
                        <Button
                          leftSection={<IconUserMinus size={15} />}
                          variant="default"
                          onClick={showWithdraw}
                          disabled={activeMembers.length < 2}
                        >
                          Retire event member
                        </Button>
                      )}
                    </>
                  )}
                  <SwitchProp
                    object={event}
                    prop="openRegistration"
                    label={t("Allow users to register themselves")}
                    mutate={mutate}
                    disabled={event.started}
                  />
                  <NumberProp
                    object={event}
                    prop="maxMembers"
                    label={t("Maximum registrations allowed")}
                    max={memberLimit}
                    min={getIsTournament(event) ? 2 : 1}
                    mutate={mutate}
                    disabled={event.started}
                  />
                  <SwitchProp
                    object={event}
                    prop="autoApproveRegistrations"
                    label={t("Automatically approve all registrations")}
                    mutate={mutate}
                    disabled={!event.openRegistration}
                  />
                  <SwitchProp
                    object={event}
                    prop="requireFullName"
                    label={t(
                      "Require first and last name for all registrations"
                    )}
                    mutate={mutate}
                    disabled={!event.openRegistration}
                  />
                  {isTournament && (
                    <SwitchProp
                      object={event}
                      prop="requireUSFA"
                      label={t(
                        "Require USFA membership number for all registrations"
                      )}
                      mutate={mutate}
                      disabled={!event.openRegistration}
                    />
                  )}
                  <SwitchProp
                    object={event}
                    prop="requireCheckIn"
                    label={t(
                      "Require users to check in before the event starts"
                    )}
                    mutate={mutate}
                    disabled={event.started}
                  />
                  <SwitchProp
                    object={event}
                    prop="requireRegistrationPayment"
                    label={t("Require users to pay a registration fee")}
                    mutate={mutate}
                    disabled={event.started || !board?.paymentsEnabled}
                  />
                  {event.requireRegistrationPayment && (
                    <NumberProp
                      fullWidth
                      object={event}
                      prop="registrationPrice"
                      label={t("Amount required for registration")}
                      // styles={{ rightSection: { width: 100 } }}
                      max={MAX_REGISTRATION_PRICE}
                      min={0}
                      // precision={2}
                      mutate={mutate}
                      precision={2}
                      icon={board && getBoardCurrencySymbol(board)}
                      disabled={event.started || !board?.paymentsEnabled}
                    />
                  )}
                  {event.requireRegistrationPayment && (
                    <SelectProp
                      label="Allow users to pay with a token"
                      object={event}
                      prop="tokenType"
                      mutate={mutate}
                      options={tokenTypeOptions}
                    />
                  )}
                  {board && !board.paymentsEnabled && organisation && (
                    <PaymentPromptBar organisation={organisation} />
                  )}
                  {board && (
                    <CheckInQR
                      disabled={!event.requireCheckIn}
                      board={board}
                      event={event}
                    />
                  )}
                  {!event.started && <ImportMembers onImport={onImport} />}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {isTournament && (
            <Accordion.Item value="permissions">
              <Accordion.Control>
                <Group>
                  <ThemeIcon variant="light">
                    <IconChevronsUp />
                  </ThemeIcon>
                  <Text size="sm">Tournament referees</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  {!hasTeamPlan && <PlanPrompt plan={PlanType.TEAM} />}
                  {event.referees.map((a) => (
                    <Group key={a}>
                      <UserDisplay user={a} />
                      <Button
                        disabled={isLoadingRemoveRef}
                        loading={isLoadingRemoveRef}
                        variant="default"
                        onClick={() =>
                          removeReferee({
                            event: event._id,
                            referee: a,
                          })
                        }
                      >
                        {t("Remove")}
                      </Button>
                    </Group>
                  ))}
                  {event.refereeInvites.length > 0 && (
                    <>
                      <label>{t("Pending referee invites")}</label>
                      {event.refereeInvites.map((a) => (
                        <Group key={a}>
                          <Text size="xs">{a}</Text>
                        </Group>
                      ))}
                    </>
                  )}
                  <Button
                    variant="default"
                    disabled={!hasTeamPlan}
                    onClick={() => {
                      trackEvent("open_modal_add_tournament_ref", {
                        board: board?._id,
                      });
                      openContextModal({
                        title: t("Add a tournament referee"),
                        modal: "addEventReferee",
                        innerProps: { event },
                      });
                    }}
                  >
                    {t("Add a referee")}
                  </Button>
                  <Text size="xs" color="dimmed">
                    {t(
                      "Event referees can record matches for this event only. They cannot affect event configuration or add members."
                    )}
                  </Text>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          {isTournament && !hasTeams && board?.sport === Sport.FENCING && (
            <Accordion.Item value="export">
              <Accordion.Control>
                <Group>
                  <ThemeIcon variant="light">
                    <IconStars />
                  </ThemeIcon>
                  <Text size="sm">USA fencing</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <TextProp
                    mutate={mutate}
                    object={event}
                    prop="usfaEventCode"
                    label="USFA event code"
                    fullWidth
                  />
                  <Button
                    disabled={!event.usfaEventCode?.length || !event.complete}
                    onClick={onClickDownloadUSFA}
                    variant="default"
                  >
                    Download USFA results file
                  </Button>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>
      </Card>
    </Container>
  );
};
export default Settings;
